import ChatServices from "apis/chat/ChatServices";
import NotificationServices from "apis/notification/NotificationServices";
import { baseUrl } from "config/axios";
import { useAuth } from "context/UseContext";
import Pusher from "pusher-js";
import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";

const PusherContext = createContext();

export const usePusher = () => useContext(PusherContext);

export const PusherProvider = ({ children }) => {

  const { user } = useAuth();

  const pusherRef = useRef(null);
  const [isPusherReady, setIsPusherReady] = useState(false);

  const [notifications, setNotifications] = useState([]);
  const [unReadCount, setUnReadCount] = useState(0);

  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);

  // *For Get Conversations
  const getConversations = async () => {
    try {
      const { data } = await ChatServices.getConversations()
      setConversations(data)
    } catch (error) {
      console.log('🚀 => getConversations => error:', error)
    }
  }

  // *For Get Notifications
  const getNotifications = async () => {
    try {
      const params = {
        page: 1,
        limit: 500
      }
      const { data, count_unread } = await NotificationServices.getNotifications(params)
      setNotifications(data)
      setUnReadCount(count_unread)
    } catch (error) {
      console.log('🚀 => getNotifications => error:', error)
    }
  }

  // *For Mark As Read
  const handleMarkAsRead = async (data) => {
    try {
      await NotificationServices.markAsRead(data)
      getNotifications()
    } catch (error) {
      console.log('🚀 => handleMarkAsRead => error:', error)
    }
  }

  const initPusher = async () => {
    try {
      const pusher = new Pusher(process.env.REACT_APP_PUSHER_KEY, {
        cluster: 'ap2',
        channelAuthorization: {
          endpoint: baseUrl + 'api/channelAuthorization',
          headers: { 'Authorization': `Bearer ${user?.token}` }
        }
      });

      pusherRef.current = pusher;
      setIsPusherReady(true);
    } catch (error) {
      console.log(' initPusher => error:', error)
    }
  };

  const handleNewNotification = useCallback((data) => {
    const newNotification = {
      ...data,
      seen: 0,
    }

    setNotifications((prev) => [newNotification, ...prev]);
    setUnReadCount((prev) => prev + 1);
  }, []);

  const updateConversation = useCallback((message) => {
    try {
      const isActive = messages.length > 0 && message?.conversation_id === messages[0]?.conversationId;

      setConversations((prev) => {
        const index = prev.findIndex(c => c?.id === message?.conversation_id);
        if (index === -1) {
          return prev;
        }

        const updatedConv = {
          ...prev[index],
          un_readCount: isActive ? 0 : (prev[index].un_readCount ?? 0) + 1,
          last_message: message
        };

        const newConversationList = [...prev];
        newConversationList.splice(index, 1);
        return [updatedConv, ...newConversationList];

      });
    } catch (error) {
      console.log(' updateMessages => error:', error)
    }
  }, [messages]);

  const handleNewMessage = useCallback((data) => {
    const newMsg = {
      conversationId: data?.conversation_id,
      date: data?.created_at,
      message: data?.message,
      attachment: data?.attachment,
      userId: data?.user_id
    };

    setMessages((prev) => {
      if (prev.length === 0 || data?.conversation_id === prev[0]?.conversationId) {
        return [...prev, newMsg];
      }
      return prev;
    });

    updateConversation(data);
  }, [updateConversation]);

  useEffect(() => {
    const ready = user && !!user.id;
    if (!ready) {
      return;
    }

    if (user) {
      initPusher();
      getConversations();
      getNotifications();
    }
  }, [user]);

  useEffect(() => {
    if (!isPusherReady || !user?.id || !pusherRef.current) {
      return;
    }

    const notificationChannel = pusherRef.current.subscribe('private-notification-channel.' + user?.id);
    notificationChannel.bind('notification.received', (data) => {
      const { notification } = data
      if (notification) {
        handleNewNotification(notification)
      }
    });

    const messageChannel = pusherRef.current.subscribe('private-messages-channel.' + user.id);
    messageChannel.bind('message.created', (data) => {
      const { message } = data
      handleNewMessage(message)
    });

    return () => {
      if (notificationChannel && messageChannel && pusherRef.current) {
        pusherRef.current.unsubscribe('private-notification-channel.' + user?.id);
        pusherRef.current.unsubscribe('private-messages-channel.' + user?.id);
        pusherRef.current.disconnect();
      }
    };
  }, [isPusherReady, user]);

  return (
    <PusherContext.Provider
      value={{
        pusherRef: pusherRef.current,
        notifications,
        unReadCount,
        handleMarkAsRead,
        conversations,
        setConversations,
        messages,
        setMessages,
        handleNewMessage
      }}
    >
      {children}
    </PusherContext.Provider>
  );
};